/**
 * The full page spinner, shown around anything that waits on the network.
 *
 * vue-loading-overlay 3 was a vue 2 library, and its programmatic loader could not be
 * carried over by @vue/compat: it appended `this.$el` from `beforeMount()`, and vue 3
 * does not assign `$el` until the subtree has rendered, so every `show()` threw
 * "Failed to execute 'appendChild' on 'Node'". Version 6 is the vue 3 line of the same
 * library - same overlay, same props - and mounts the loader itself.
 *
 * `useLoading()` needs neither an app nor an active component instance, which is what
 * this module needs: the spinner is driven from resources and store actions, not only
 * from components.
 */
import * as vueLoadingOverlay from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

const $loading = vueLoadingOverlay.useLoading({
    color: '#fff',
    backgroundColor: '#000',
    loader: 'spinner',
    opacity: 0.85,
    width: 94,
    height: 94,
});

// the spinner is reference counted: overlapping requests each show it, and it goes away
// when the last of them is done
let ACTIVE_LOADERS = [];

let loader = undefined;

const showLoader = (name = 'default', options = {}) => {
    // show loader only if not already active;
    ACTIVE_LOADERS.push(name);
    if (loader === undefined) {
        loader = $loading.show(options);
    }
};

const hideLoader = (name = 'default') => {
    ACTIVE_LOADERS = ACTIVE_LOADERS.filter(l => l !== name);
    if (ACTIVE_LOADERS.length === 0) {
        if (loader !== undefined) {
            loader.hide();
            loader = undefined;
        }
    }
};

/**
 * The spinner is one module singleton, imported wherever it is driven.
 *
 * It used to be reached two ways - `Vue.loadingSpinner` off the compat global for the
 * factories and the resources, `this.$loadingSpinner` for components - and the install
 * built a separate literal for each, so the two were different objects around the same
 * pair of functions. There is one object now, and the global half is an import.
 */
const loadingSpinner = {
    show: showLoader,
    hide: hideLoader,
};

// `this.$loadingSpinner` for the components and the templates, which have nothing to
// import from. `app.config.globalProperties` is where a vue 3 app keeps those - compat's
// `Vue.prototype` wrote into every app at once, which is what GLOBAL_PROTOTYPE was
const VueLoadingSpinner = {
    install(app) {
        app.config.globalProperties.$loadingSpinner = loadingSpinner;
    }
};

export default VueLoadingSpinner;
export { loadingSpinner };
